import { inject, Injectable } from '@angular/core';
import { Quiz, highlightText } from 'funnel/src/app/data/quiz';
import type { Book } from 'shared/src/types/funnel-sample-content';
import {
  BookId,
  defaultBookIds,
  fictionInterestBookIdsMap,
  interleaveBookIds,
  motivationBookIdMap,
  nonFictionInterestBookIdsMap,
} from '../../../../funnel/src/app/data/books';
import {
  FunnelFlowService,
  QuizStepKey,
} from '../../../../funnel/src/app/services/funnel-flow.service';
import { FunnelFlowConfig } from 'funnel/src/app/flow/flow-config';
import { isConditionMet } from 'funnel/src/app/flow/flow-engine';
import { kindleFunnelFlow, kindleMainFlow } from '../kindle-funnel.flow';
import { KindleResultMessageService } from './kindle-result-message.service';

declare const $localize: any;

@Injectable({
  providedIn: 'root',
})
export class KindleFunnelFlowService extends FunnelFlowService {
  private readonly kindleResultMessageService = inject(KindleResultMessageService);

  protected override getFlowConfig(): FunnelFlowConfig {
    return this.isOnMainFlow() ? kindleMainFlow : kindleFunnelFlow;
  }

  /**
   * Visitors without Kindle books in the `main` cell of kindle_no_books_flow_nov_2026 continue on
   * the main funnel: its screens, copy, result and paywall copy. The project stays kindle-funnel,
   * so pixels, placements and upsells do not change.
   */
  isOnMainFlow(): boolean {
    return (
      this.getAbCell('kindle_no_books_flow_nov_2026') === 'main' &&
      isConditionMet({ step: 'hasKindleBooks', is: ['no'] }, this.getWebQuizAnswers())
    );
  }

  protected override getDefaultIntroTitle(): string {
    return (
      $localize`:@@kindleAgeTitlePrefix:Let's set up your ` +
      highlightText($localize`:@@kindleAgeTitleHighlight:Kindle listening plan`)
    );
  }

  override customizeStepData(key: QuizStepKey): Quiz | undefined {
    if (key === 'hasKindleBooks') {
      // The first screen: the intro title takes the headline, so the question drops to the
      // subtitle.
      return {
        ...this.steps.hasKindleBooks,
        title: this.getIntroTitle(),
        subtitle: this.steps.hasKindleBooks.title,
      };
    }

    if (this.isOnMainFlow()) {
      return super.customizeStepData(key);
    }

    if (key === 'amountInfo') {
      const data = super.customizeStepData(key) || this.steps.amountInfo;
      const techCrunchQuote = $localize`:@@amountInfoTechCrunchQuote:Peech is a text-to-speech app that actually sounds good`;
      const mediaCiteSecondPart = $localize`:@@kindleAmountInfoCiteSuffix:- a smoother way to keep long reads moving when your day does not leave room to sit with the page.`;

      return {
        ...data,
        mediaCite: `${techCrunchQuote} ${mediaCiteSecondPart}`,
      };
    }

    if (key === 'goalMotivations') {
      const data = super.customizeStepData(key) || this.steps.goalMotivations;

      return {
        ...data,
        title:
          $localize`:@@kindleGoalMotivationsTitlePrefix:What do you want to achieve by ` +
          highlightText($localize`:@@kindleReadingMoreBooks:reading more books`) +
          $localize`:@@kindleGoalMotivationsTitleSuffix:?`,
      };
    }

    if (key === 'goalInfo') {
      const data = super.customizeStepData(key) || this.steps.goalInfo;

      return {
        ...data,
        title:
          $localize`:@@kindleGoalInfoTitlePrefix:You are 33% more likely* to follow through when your ` +
          highlightText($localize`:@@kindleReadingPlan:reading plan`) +
          $localize`:@@kindleGoalInfoTitleSuffix: is clear`,
        subtitle: this.getGoalInfoSubtitle(),
      };
    }

    if (key === 'timeForReading') {
      return {
        ...this.steps.timeForReading,
        title:
          $localize`:@@kindleTimeForReadingTitlePrefix:Do Kindle books pile up because you ` +
          highlightText($localize`:@@kindleCannotAlwaysRead:can't always sit down to read`) +
          $localize`:@@kindleTimeForReadingTitleSuffix:?`,
      };
    }

    if (key === 'audioExperienceInfo') {
      const data = super.customizeStepData(key) || this.steps.audioExperienceInfo;

      return {
        ...data,
        title:
          $localize`:@@kindleAudioInfoTitlePrefix:Your ` +
          highlightText($localize`:@@kindleAudioInfoTitleHighlight:Kindle library`) +
          $localize`:@@kindleAudioInfoTitleSuffix: can become audio`,
        subtitle: $localize`:@@peechWorld1ReaderApp:With Peech, you do not need to buy a separate Audible version just to listen.\n\nImport supported Kindle books, documents, and long reads you already have, then listen with natural voices built for long sessions.`,
      };
    }

    if (key === 'book1' || key === 'book2' || key === 'book3' || key === 'yourBook') {
      const data = super.customizeStepData(key) || this.steps[key];

      return {
        ...data,
        title: $localize`:@@kindleBookListenTitle:Would this feel good as audio from your Kindle library?`,
      };
    }

    if (key === 'prediction') {
      const data = super.customizeStepData(key) || this.steps[key];

      return {
        ...data,
        title: $localize`:@@kindlePredictionTitle:We predict you will finish more chapters`,
        subtitle: $localize`:@@kindlePredictionSubtitle:Peech can help you get through more of the books you already own by turning spare moments into listening time.`,
      };
    }

    return super.customizeStepData(key);
  }

  protected override getResultStep(): Quiz {
    if (this.isOnMainFlow()) {
      return super.getResultStep();
    }

    const { messages, metricProperties } = this.kindleResultMessageService.generateMessages({
      answers: this.getWebQuizAnswers(),
      motivationIds: this.getMotivationIds(),
      fictionInterestIds: this.getFictionInterestIds(),
      nonFictionInterestIds: this.getNonFictionInterestIds(),
    });

    this.trackResultMetricOnce('web_kindle_result_metric_shown', metricProperties);

    return {
      ...this.steps.result,
      title:
        $localize`:@@kindleResultTitlePrefix:Your ` +
        highlightText($localize`:@@kindleResultTitleHighlight:Kindle listening plan`) +
        $localize`:@@kindleResultTitleSuffix: is ready`,
      messages,
    };
  }

  protected override getGoalInfoSubtitle(): string {
    if (this.isOnMainFlow()) {
      return super.getGoalInfoSubtitle();
    }

    const message = $localize`:@@kindleGoalInfoSubtitle:Join 900,000+ readers who use Peech to turn the books they mean to finish into audio that fits walks, chores, commutes, and screen-free breaks.`;
    const note = this.getGoalInfoSubtitleNote();

    return `${message}<br/><i class="fs-12 muted-color">${note}</i>`;
  }

  protected override getRelevantBooks(): Book[] {
    if (this.isOnMainFlow()) {
      return super.getRelevantBooks();
    }

    const relevantBookIds: BookId[] = [];
    const fictionBookIds: BookId[] = [];
    const nonFictionBookIds: BookId[] = [];
    const motivationBookIds: BookId[] = [];
    const otherFictionBookIds: BookId[] = [];

    const addBookId = (target: BookId[], bookId: BookId | undefined) => {
      if (bookId && !target.includes(bookId)) {
        target.push(bookId);
      }
    };
    const addBookIds = (target: BookId[], bookIds: readonly BookId[]) => {
      bookIds.forEach(bookId => addBookId(target, bookId));
    };

    this.getFictionInterestIds().forEach(interestId => {
      if (interestId === 'other') {
        addBookIds(otherFictionBookIds, fictionInterestBookIdsMap[interestId]);
      }
    });

    addBookIds(
      fictionBookIds,
      interleaveBookIds(
        this.getFictionInterestIds()
          .filter(interestId => interestId !== 'other')
          .map(interestId => fictionInterestBookIdsMap[interestId] || [])
      )
    );

    this.getNonFictionInterestIds().forEach(interestId => {
      addBookIds(nonFictionBookIds, nonFictionInterestBookIdsMap[interestId]);
    });

    this.getMotivationIds().forEach(motivationId => {
      addBookId(motivationBookIds, motivationBookIdMap[motivationId]);
    });

    const addRelevantBookId = (bookId: BookId | undefined) => addBookId(relevantBookIds, bookId);
    const addRelevantBookIds = (bookIds: readonly BookId[]) => bookIds.forEach(addRelevantBookId);

    if (fictionBookIds.length > 0) {
      addRelevantBookId(fictionBookIds[0]);
      addRelevantBookId(fictionBookIds[1]);
      addRelevantBookId(nonFictionBookIds[0]);
      addRelevantBookIds(motivationBookIds);
      addRelevantBookIds(fictionBookIds.slice(2));
      addRelevantBookIds(nonFictionBookIds.slice(1));
    } else {
      addRelevantBookIds(nonFictionBookIds);
      addRelevantBookIds(motivationBookIds);
      addRelevantBookIds(otherFictionBookIds);
    }

    addRelevantBookIds(defaultBookIds);

    return this.sampleContentService.getBooks(relevantBookIds);
  }

  protected override findDiverseBook(candidates: Book[], selectedBooks: Book[]): Book | undefined {
    if (this.isOnMainFlow()) {
      return super.findDiverseBook(candidates, selectedBooks);
    }

    return candidates.find(candidate => !selectedBooks.includes(candidate));
  }
}
